/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { v4 as uuidV4 } from 'uuid';

import { DocumentVersionFlag } from '../documentVersionFlags/documentVersionFlags';
import { SlugInfoType, standardGuides } from './chatSlice';
import {
  DocumentVersionWithoutGeneratedFieldsType,
  PageGuideType,
  PageMultipleQuestionsType,
  PageTypeEnum,
  PageSwimLaneType,
  PageType,
  PageFhirType,
  FhirPredefinedWidget,
  PageCalculatorType,
  PageOptionExclusiveType,
  PageOptionMultipleChoiceType,
  PageIfType,
  LinkSlugType,
  FhirPageWidgetType,
  PageQuestionExclusiveType,
} from '../algorithm/types';
import { pageCanHaveNextLink, questionCanHaveOptions } from '../algorithm/runAlgorithm';
import { compiledHtmlConvertPlain } from '../utils/htmlToText';
import { createRandomAlphaNumericId, unescapeHtml } from '../utils';
import { slugFoundCb } from './convertPearlApiDocumentVersionToDocumentVersion';
import { SEARCH_PARAM } from './useHooks';
import { fixupWidgetId } from '../Fhir/fhir_network';
import { IPearlLibrary } from '../Login/userSlice';
import { libraryIdToDetailsMap } from '../libraryUtils';
import { getLegacyKeyForExclusiveQuestion, getKeyForExclusiveQuestion } from '../algorithm/question_utils';
import { resolveFhirPredefinedWidgetId } from '../Fhir/fhir_utils';
import { updateCopyEdit } from './updateCopyEdit';

/**
 * fixup a document version when saving and loading.  This assumes that the pages have been fixed
 * up already, because it checks the pages flag `isContextualized` and `pageHasTrackedChanges`.
 * Example to fixup document and pages.  In practice we can't use this directly anywhere, because
 * when loading a document we need to dedupe pages (not really needed anymore - it's from when
 * we supported multiple audiences, but I don't want to remove that code).
 * And when saving a document, we only fixup dirty pages.
 *
 * @example
 * ```ts
 *   function fixupDocumentVersionAndPages<T extends DocumentVersionWithoutGeneratedFieldsType>(docVersion: T): T {
 *     Object.values(docVersion.pages)?.map((page) => fixupPage(page));
 *     return fixupDocumentVersion(docVersion);
 *   }
 * ```
 */
export function fixupDocumentVersion<T extends DocumentVersionWithoutGeneratedFieldsType>(docVersion: T): T {
  docVersion.pages = cloneJson(docVersion.pages);
  const dirty: string[] = [];
  const isContextualized = Object.values(docVersion.pages).some((page) => page.isContextualized);
  const pageHasTrackedChanges = Object.values(docVersion.pages).some((page) => page.hasTrackedChanges);
  docVersion.tags = docVersion.tags?.filter((x) => !!x) || [];
  const tags = docVersion.tags;
  docVersion.flags = docVersion.flags?.filter((x) => !!x) || [];
  const flags = docVersion.flags;
  const flagContextualizedindex = flags.indexOf(DocumentVersionFlag.contextualized);
  const modifiedPageIdsOnLoad = migrateLegacyQuestionKeyReferences(docVersion, dirty);

  if (modifiedPageIdsOnLoad.length > 0) {
    docVersion.modifiedPageIdsOnLoad = modifiedPageIdsOnLoad;
  } else {
    delete docVersion.modifiedPageIdsOnLoad;
  }

  // First deal with the isContextualized (whether we really have contextualization in the document)
  // This just sets the flag, and doesn't change the tags. We change tags after this.
  if (!isContextualized) {
    if (flagContextualizedindex !== -1) {
      flags.splice(flagContextualizedindex, 1);
      dirty.push('Removed contextualized flag, because no contextualization was found in document');
    }
  } else {
    // isContextualized
    if (flagContextualizedindex === -1) {
      flags.push(DocumentVersionFlag.contextualized);
      flags.sort();
      dirty.push('Added contextualized flag, because some contextualization was found in the document');
    }
  }
  if (pageHasTrackedChanges) {
    if (!flags.includes(DocumentVersionFlag.tracked_changes)) {
      flags.push(DocumentVersionFlag.tracked_changes);
      flags.sort();
      dirty.push('Added tracked_changes flag, because some pages have tracked changes');
    }
  } else {
    if (flags.includes(DocumentVersionFlag.tracked_changes)) {
      flags.splice(flags.indexOf(DocumentVersionFlag.tracked_changes), 1);
      dirty.push('Removed tracked_changes flag, because no pages have tracked changes');
    }
  }

  const document = JSON.parse(JSON.stringify(docVersion.document));
  fixupLibraryFlags(document.library, flags, dirty);

  docVersion.document = document;
  docVersion.tags = tags;
  docVersion.flags = flags;

  fixupDocumentVersionContextualizedTag(docVersion, dirty);
  if (dirty.length > 0) {
    docVersion.modifiedOnLoad = dirty;
  } else {
    delete docVersion.modifiedOnLoad;
  }
  if (dirty.length > 0) {
    console.log(`Document "${docVersion.title}" modified when loading because: `, dirty.join(', '));
  }
  return docVersion;
}

export function fixupLibraryFlags(library: IPearlLibrary | undefined, flags: string[], dirty: string[]) {
  if (!library || !library?.id) return;
  const libraryDetails = libraryIdToDetailsMap[library.id];
  if (libraryDetails) {
    if (!library.name && libraryDetails.name) {
      library.name = libraryDetails.name;
    }
    if (libraryDetails.flag && !flags?.includes(libraryDetails?.flag || '')) {
      flags.push(libraryDetails.flag);
      flags.sort();
      dirty.push(`Added ${libraryDetails.flag} flag, because library is ${libraryDetails.name}`);
    }
  }
  for (const libraryIdToDetail of Object.values(libraryIdToDetailsMap)) {
    if (
      libraryIdToDetail.flag &&
      libraryIdToDetail.flag !== libraryDetails?.flag &&
      flags.includes(libraryIdToDetail.flag)
    ) {
      flags.splice(flags.indexOf(libraryIdToDetail.flag), 1);
      dirty.push(`Removed ${libraryIdToDetail.flag} flag, because library is no longer ${libraryIdToDetail.name} only`);
    }
  }
}

/** This is only useful for unit tests, because normally a document will be fixed up on load */
export function fixupDocumentVersionAndPages<T extends DocumentVersionWithoutGeneratedFieldsType>(docVersion: T): T {
  Object.entries(docVersion.pages).forEach(([key, page]) => {
    docVersion.pages[key] = fixupPage(page, true, slugFoundCb);
  });
  return fixupDocumentVersion(docVersion);
}

export function fixupDocumentVersionContextualizedTag<T extends DocumentVersionWithoutGeneratedFieldsType>(
  docVersion: T,
  dirty?: string[]
) {
  const { tags, flags } = docVersion;
  const hasContextualizedTag = tags?.includes(DocumentVersionFlag.contextualized);
  const isContextualized = flags?.includes(DocumentVersionFlag.contextualized);
  // If the user has contextualized_forced, then treat this as contextualized. This is useful
  // for triages where we have no contextualization in the document itself, but we link to
  // contextualized documents so it's useful to show it as contextualized.
  const hasForcedContextualization = flags?.includes(DocumentVersionFlag.contextualized_forced);
  // Now deal with the tags. This time care about forced contextualization.
  if (!isContextualized && !hasForcedContextualization) {
    if (hasContextualizedTag) {
      // We have the contextualized tag, but the document is not contextualized.
      // So remove it
      docVersion.tags = tags?.filter((x) => x !== DocumentVersionFlag.contextualized);
      dirty?.push('Removed contextualized tag, because no contextualization was found in document');
    }
  } else {
    // isContextualized || hasForcedContextualization
    if (!hasContextualizedTag) {
      docVersion.tags = [...(tags || []), DocumentVersionFlag.contextualized];
      docVersion.tags.sort();
      dirty?.push('Added contextualized tag, because some contextualization was found in the document');
    }
  }
}

export function findReplaceContent(page_: PageType, find: string, replace: string): PageType {
  const page: PageType = cloneJson(page_);
  const r = new RegExp(find, 'g');
  const p = forEachPageContents({ ...page }, (content) => content?.replace(r, replace)).page;
  return p;
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

export function stripEphemeralEditorMetadata(content: string): string {
  return content.replace(/\sdata-list-item-id=(?:"[^"]*"|'[^']*')/gi, '');
}

/** Normalize one page for saving or loading.
 *
 * This function clones the incoming page with JSON stringify/parse before making
 * changes, so callers must use the returned page. The input object is not updated.
 * Page data is persisted as JSON, so `fixupPage` expects JSON-compatible page
 * objects rather than runtime-only values such as Dates, Maps, Sets, functions,
 * or class instances.
 *
 * When saving, `fixupPage` also adds backwards-compatible fields still used by
 * older clients. When loading, it removes or normalizes legacy fields into the
 * current page shape.
 *
 * cbLinkFound is a callback that is called when a slug is found in a link.  This is set to
 * @see slugFoundCb
 *
 */
export function fixupPage<T extends PageType>(
  page: T,
  saving: boolean,
  cbLinkFound?: (slug: SlugInfoType) => SlugInfoType | undefined, /// @see slugFoundCb
  cbProblemFound?: (problem: string) => void,
  predefinedWidgetsMap?: Record<string, FhirPredefinedWidget>
): T {
  page = cloneJson(page);
  delete page.isContextualized; // We will set this in fixupPageContents
  delete page.hasTrackedChanges; // Recompute across all content fields in this page
  page = fixupPageGuides(page);
  page = fixupPageIds(page);
  if (page.fhir) {
    page.fhir = fixupPageFhir(page.fhir);
    migrateDeprecatedFhirWidgets(page.fhir, predefinedWidgetsMap);
  }
  page = forEachPageContents(page, (content, page, options) => {
    return fixupPageContent(content, page, cbLinkFound, cbProblemFound, options);
  }).page;
  page = fixupPageNextLinks(page, cbLinkFound);
  page = fixupQuestions(page, !!saving, cbLinkFound);
  // console.log('Calling fixupPage, saving:', saving, page);
  return page;
}

export function forEachPageContents<T extends PageType>(
  page: T,
  callback: (
    content: string | undefined,
    page: T,
    options?: { unwrapOuterDivs?: boolean; disableFhir?: boolean }
  ) => string | undefined
): { page: T; changedCount: number } {
  let changedCount = 0;
  function callbackAndCount(
    content: string | undefined,
    page: T,
    options?: { unwrapOuterDivs?: boolean; disableFhir?: boolean }
  ) {
    const new_content = callback(content, page, options);
    if (new_content !== content) {
      changedCount += 1;
    }
    return new_content;
  }
  page.content &&= callbackAndCount(page.content, page);
  (page as PageMultipleQuestionsType).questions?.forEach((question) => {
    question.tooltip &&= callbackAndCount(question.tooltip, page, { disableFhir: true });
    question.questionHtml &&= callbackAndCount(question.questionHtml, page, { unwrapOuterDivs: true });
    if (questionCanHaveOptions(question)) {
      question.options?.forEach((option) => {
        option.tooltip &&= callbackAndCount(option.tooltip, page, { disableFhir: true });
      });
    }
  });
  (page as PageSwimLaneType).lanes?.forEach((lane) => {
    lane.content &&= callbackAndCount(lane.content, page);
  });
  return { page, changedCount };
}

export function fixupPageContent(
  content: string | undefined, // The html of the page content.
  page: PageType | undefined, // This is only needed for fixing up <fhir> tag metadata
  cbLinkFound?: (slug: SlugInfoType) => SlugInfoType | undefined,
  cbProblemFound?: (problem: string) => void,
  options?: { unwrapOuterDivs?: boolean }
): string | undefined {
  if (!content) {
    return content;
  }
  if (typeof content !== 'string') {
    console.error('Invalid content! Content type is', typeof content, JSON.stringify(content));
  }
  if (!content.includes) {
    console.error('Invalid content! Content does not have includes function', content);
  }
  // It would be good to call sanitizeHtml here, but it's really slow and can cause out of memory.
  // Note we have to call twice otherwise it reverses the attributes - a stupid bug in
  // DOMPurify.
  // content = sanitizeHtml(sanitizeHtml(content));
  // keep doing this until the content doesn't change
  let lastContent;
  let numIters = 0;
  let div;
  do {
    if (numIters++ > 10) {
      console.error('Infinite loop in fixupPageContent\n', lastContent, '\n', content);
      break;
    }
    lastContent = content;
    content = unicodeToChar(content);
    content = removeBadHtml(content);
    content = removeTooltipFromActionIcon(content);
    content = combineBulletPoints(content);
    content = fixChoosingWiselyLinks(content);
    content = combineSpans(content);

    // Now, convert to dom to make it easier to manipulate and clean, the convert back
    div = document.createElement('div');
    div.innerHTML = content;
    if (options?.unwrapOuterDivs) {
      unwrapOuterDivs(div);
    }
    const { duplicatedFhirIds } = cleanupDom(div);
    if (page?.fhir && duplicatedFhirIds.length > 0) {
      duplicateRemappedFhirConfig(page.fhir, duplicatedFhirIds);
    }
    fixupYoutubeMediaTags(div);
    if (page?.fhir) {
      updateFhirAttributes(div, page.fhir);
    }
    updatePearltag(div);
    updateAction(div);
    updateLi(div);
    updateTextContent(div);
    updateALinks(div, cbLinkFound, cbProblemFound);
    updateEmbeddocumentpage(div, cbLinkFound, cbProblemFound);
    updateCopyEdit(div);

    content = div.innerHTML;
  } while (content !== lastContent);
  if (page) page.isContextualized ||= content.includes('<fhir');
  if (page) page.hasTrackedChanges ||= pageHasTrackedChanges(div);
  return content;
}

/** if the div has only one child and it is a div and has no attributes, unwrap it.
 *
 * This is for questionHtml and we want to unwrap it so the icon tooltip lines up nicely with the end
 * of the question text.
 */
function unwrapOuterDivs(div: HTMLDivElement | undefined) {
  if (!div) return;
  while (div.children.length === 1 && div.children[0]!.tagName === 'DIV' && div.children[0]!.attributes.length === 0) {
    const child = div.children[0] as HTMLDivElement;
    while (child.firstChild) {
      div.appendChild(child.firstChild);
    }
    div.removeChild(child);
  }
}

/** Check for any ckeditor tracked changes.
 *  These are:
 *    <suggestion-start ..>
 *    <suggestion-end ..>
 * or any data with attributes:
 *    data-suggestion-start-before
 *    data-suggestion-start-after
 *    data-suggestion-end-before
 *    data-suggestion-end-after
 */
function pageHasTrackedChanges(div: HTMLDivElement | undefined): boolean {
  if (!div) return false;
  return (
    div.querySelectorAll(
      'suggestion-start, suggestion-end, [data-suggestion-start-before], [data-suggestion-start-after], [data-suggestion-end-before], [data-suggestion-end-after]'
    ).length > 0
  );
}
/** Remove class="li-arrow" from <li> elements */
function updateLi(div: HTMLDivElement) {
  div.querySelectorAll('li.li-arrow').forEach((li) => {
    li.classList.remove('li-arrow');
    // if the class is now empty, remove the class attribute
    if (li.classList.length === 0) {
      li.removeAttribute('class');
    }
  });
}

/** If we have: <oembed url="https://www.youtube.com/embed/ouF-H35atOY"></oembed>
 *  This is wrong because the url should be the watch?v= URL
 *
 */
function fixupYoutubeMediaTags(div: HTMLDivElement) {
  div.querySelectorAll('oembed').forEach((oembed) => {
    const url = oembed.getAttribute('url');
    if (url && url.includes('youtube.com/embed/')) {
      const videoId = url.split('/embed/')[1]?.split('?')[0];
      oembed.setAttribute('url', `https://www.youtube.com/watch?v=${videoId}`);
    }
  });
}

/** Add an id to every pearltag if it doesn't have one, or it is literally 'undefined' */
function updatePearltag(div: HTMLDivElement) {
  div.querySelectorAll('pearltag:not([id])').forEach((pearltag) => {
    pearltag.id = 'p' + uuidV4();
  });
  div.querySelectorAll('pearltag[id="undefined"]').forEach((pearltag) => {
    pearltag.id = 'p' + uuidV4();
  });
}

function removeWhitespaceTextNodes(el: Element) {
  Array.from(el.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()) {
      el.removeChild(node);
    }
  });
}

/** Add an id to every action if it doesn't have one, or it is literally 'undefined',
 *  and make sure actioncontent.type matches the action.type
 */
function updateAction(div: HTMLDivElement) {
  div.querySelectorAll('action').forEach((action) => {
    removeWhitespaceTextNodes(action); // Action content should be the only child of action, so remove any whitespace text nodes that can mess things up
    if (!action.id || action.id === 'undefined') {
      action.id = 'a' + uuidV4();
    }
    const type = action.getAttribute('type');
    if (type === 'undefined') {
      // Not really a good default - but set to info
      action.setAttribute('type', 'info');
    }
    // actioncontent should be the one and only child of action.  If it's not, then wrap all children in one
    if (action.children.length !== 1 || action.children[0]!.tagName !== 'ACTIONCONTENT') {
      const actionContent = document.createElement('actioncontent');
      while (action.firstChild) {
        actionContent.appendChild(action.firstChild);
      }
      action.appendChild(actionContent);
    }
    const actionContent = action.childNodes[0] as HTMLElement;
    // Check it is actually an HTMLElement
    if (!(actionContent instanceof HTMLElement) || actionContent.tagName !== 'ACTIONCONTENT') {
      // happens with AI generated content that can mess things up
      console.error(
        'Invalid action content! Action content should be the only child of action and should be an HTMLElement with tagName ACTIONCONTENT. Found:',
        actionContent
      );
      console.log('Invalid action content outerHTML:', div?.outerHTML);
    } else {
      // Make sure actionContent has a type that matches the action type
      if (actionContent.getAttribute('type') !== type) {
        actionContent.setAttribute('type', type || 'info');
      }
    }
  });
}

/** Apply the desired AMA style guide where we can do automatically
 *  Only apply to text nodes
 */
function updateTextContent(div: HTMLDivElement) {
  div.querySelectorAll('*').forEach((el) => {
    el.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        let content = node.textContent || '';
        const newContent = applyAmaStyleGuide(content);
        if (newContent !== content) {
          node.textContent = newContent;
        }
      }
    });
  });
}

function applyAmaStyleGuide(content: string): string {
  // Replace "e.g.," with "eg,"
  //content = content.replace(/\be\.g\.,*/g, 'eg,');
  //// Replace "i.e.," with "ie,"
  //content = content.replace(/\bi\.e\.,*/g, 'ie,');
  //// Replace "et al." with "et al"
  //content = content.replace(/\bet al\./g, 'et al');
  // Replace "3 - 5" with "3–5". Remove spaces, and use endash, not hyphen nor em dash (might be html encoded, &#8211; or &#8212; or &emdash; or &ndash;)
  //content = content.replace(/(\d+)\s*([–—-]|&#8211;|&#8212;|&emdash;|&ndash;)\s*(\d+)/g, '$1–$3');
  return content;
}

/** Clean up the link.
 *
 * - Remove http://pearl-project.io from the start of the url, since that's our website
 * - If it's like /page/? or /abc/def/page/?  or ../../abc/def/page/? then remove all that and change to /chat
 * - Remove any ../../ from the start of the url
 * - If it starts with tel: or mailto: then do nothing
 * - If the link is a chat? link, then make sure it starts with /
 * - If the link is a persona= link, then make sure it starts with /chat?
 * - If the link is a ? link, then make sure it starts with /chat
 * - If the link is a # link, then just make sure it's trimmed
 */
export function fixupLink(href: string) {
  if (!href) return '';
  href = href.trim();
  if (href.startsWith('tel:') || href.startsWith('mailto:')) {
    return href;
  }
  if (href.startsWith('#https://') || href.startsWith('#http://')) {
    // This is a creator mistake, but one that messes everything up - so let's fix it
    href = href.substring(1);
  }
  // Support old version pearl-project.io links and newer medpearl.com links
  href = href.replace(
    /^https?:[/][/]([^/]*pearl-project[.]io|localhost:?\d*|[^/]*medpearl[.]com)[/](prov[/])?(chat|page)[/]?\??/,
    '/chat?'
  );
  // If it still starts with https?:// then it's an external link
  if (href.startsWith('https://') || href.startsWith('http://')) {
    href = bingImageToDirectLink(href) || href;
    return href;
  }
  href = href.replace(/^([.][.][/])+/, '/');
  href = href.replace(/^\/?[a-zA-Z0-9-]*\/[a-zA-Z0-9-]*\/page\/\?/, '/chat?');
  if (href.match(/^[a-zA-Z%0-9- &]+(#[a-zA-Z%0-9- &]+)?$/)) {
    href = '#' + href.replace(' #', '#').replace(/ /g, '%20');
  }
  if (href.startsWith('?')) {
    href = '/chat' + href;
  } else if (href.startsWith('&slug=')) {
    href = '/chat?' + href.substring(1);
  } else if (href?.startsWith('chat?')) {
    href = '/' + href;
  } else if (href.startsWith('persona=')) {
    href = '/chat?' + href;
  } else if (href.startsWith('/') || href.startsWith('http')) {
    // do nothing
  } else if (href.startsWith('#')) {
    // Split by #, then trim each part, then rejoin
    const parts = href.split('#').map((part) => part.trim());
    href = parts.join('#');
  } else if (!href.includes(':') && href.match(/[.]com|org|net|edu|gov|mil|info|biz|name|pro|aero|coop|museum/)) {
    href = 'http://' + href;
  }
  if (href.startsWith('/chat?')) {
    href = href.replace(/&amp;/g, '&');
  }
  return href;
}

/** Assumes you call fixupLink first */
export function isInternalLink(href: string) {
  return href && (href.startsWith('#') || href.startsWith('/'));
}

/** Replace e.g.  </span> &nbsp;<span>  with the whitespace in the middle
 */
function combineSpans(content: string): string {
  content = content.replace(/<\/span>((?:\s|&nbsp;)*)<span>/g, '$1');
  return content;
}

/** Assumes you've already called fixupLink first,
 *  and checked isInternalLink
 */
export function hrefToLinkInfo(href: string): SlugInfoType {
  const url = new URL(href, 'http://localhost');
  // Get the slug= parameter from the query string
  const slug = url.searchParams.get(SEARCH_PARAM.SLUG);
  const page = url.searchParams.get(SEARCH_PARAM.PAGE);
  const ret: SlugInfoType = {};
  if (slug) {
    ret.slug = slug;
  }
  if (page) {
    ret.pageName = page;
  }
  // Get the hash from the url
  const hash = url.hash?.substring(1);
  if (hash) {
    // Split by a second # if there's one.  Because it might be like #foo#bar  mean document foo, page bar
    const parts = hash.split('#', 2);
    if (parts[0]) {
      ret.oldDocName = unescapeHtml(decodeURIComponent(parts[0]).trim());
    }
    if (!ret.pageName && parts[1]) {
      ret.pageName = unescapeHtml(decodeURIComponent(parts[1]).trim());
    }
  }
  return ret;
}

// Assumes isInternalLink(href) is true
function updateHrefWithLinkInfo(href: string, linkInfo: SlugInfoType): string {
  const url = new URL(href, 'http://localhost'); // Need a dummy base URL
  let hash = '';
  if (linkInfo.slug) {
    url.searchParams.set(SEARCH_PARAM.SLUG, linkInfo.slug);
  } else if (linkInfo.currentDocName || linkInfo.oldDocName) {
    url.searchParams.delete(SEARCH_PARAM.SLUG);
    hash = encodeURI(linkInfo.currentDocName || linkInfo.oldDocName!);
    if (linkInfo.pageName) {
      hash += '#' + encodeURI(linkInfo.pageName);
    }
  } else {
    // There's really nothing we can do
    return href;
  }
  if (linkInfo.pageName) {
    url.searchParams.set(SEARCH_PARAM.PAGE, linkInfo.pageName);
  } else {
    url.searchParams.delete(SEARCH_PARAM.PAGE);
  }
  url.hash = hash;
  url.pathname = '/chat';

  // Return only the pathname + search + hash
  return url.pathname + url.search + url.hash;
}

/** Add slug information to links if they don't currently have it,
 *  and update the SEE ALSO text.
 */
function updateALinks(
  div: HTMLDivElement,
  cbLinkFound?: (slug: SlugInfoType) => SlugInfoType | undefined,
  cbProblemFound?: (problem: string) => void
) {
  div.querySelectorAll('a').forEach((a) => {
    // If it has no text and no children, then remove it
    if (!a.textContent?.trim() && !a.children.length) {
      a.remove();
      return;
    }
    // delete any data-* in a.attributes that are not data-slug or data-doc-name
    ['data-cthref', 'data-saferedirecturl', 'data-redirect', 'data-href', 'data-cthref', 'data-cturl', 'ping'].forEach(
      (attr) => {
        if (a.hasAttribute(attr)) {
          a.removeAttribute(attr);
        }
      }
    );

    // Unwrap all the span tags
    a.querySelectorAll('span:not([style])').forEach((span) => {
      span.outerHTML = span.innerHTML;
    });
    // And remove all <u> tags
    a.querySelectorAll('u').forEach((u) => {
      u.outerHTML = u.innerHTML;
    });
    let href = a.getAttribute('href');
    if (!href) {
      // Shouldn't happen, because we clean up and remove such links in removeBadHtml
      console.log('Invalid a link:', a.outerHTML);
      return;
    }
    href = fixupLink(href); // Note this doesn't convert "#foo" to slug links. That's done below in "if (isInternalLink).."
    a.setAttribute('href', href);

    /*  If have a two links next to each other that point to the same place, then combine them
     * e.g. <a href="g.com">foo</a> <a href="g.com">bar</a>  -> <a href="g.com">foo bar</a>
     */
    const next = a.nextElementSibling;
    if (next && next.tagName === 'A' && next.getAttribute('href') === href) {
      // Check what text we have between them
      if (a.nextSibling?.nodeType === Node.TEXT_NODE) {
        // Check if it is only whitespace or &nbsp; followed by our next link
        const text = a.nextSibling.textContent;
        if (a.nextSibling?.nextSibling === next && (!text || /^(?:\s|&nbsp;)*$/.test(text))) {
          a.textContent += (text || '') + next.textContent;
          a.nextSibling.remove(); // Remove the whitespace node
          next.remove(); // Remove the next <a>
        }
      } else {
        // If there's no intervening text, then just combine the textContent
        a.textContent += '' + next.textContent;
        next.remove();
      }
    }

    if (isInternalLink(href)) {
      a.setAttribute('target', '_self');
      const oldDocName = a.getAttribute('data-doc-name');
      a.classList.add('internal-link');
      a.classList.remove('external-link');
      let newLinkInfo: SlugInfoType | undefined;
      const oldLinkInfo = hrefToLinkInfo(href);
      if (oldDocName) {
        oldLinkInfo.oldDocName ||= oldDocName;
      }
      if (a.getAttribute('data-slug')) {
        oldLinkInfo.slug ||= a.getAttribute('data-slug')!;
      }
      if (oldLinkInfo && (oldLinkInfo.oldDocName || oldLinkInfo.slug)) {
        // No point calling cbLinkFound if there's no oldDocName or slug
        newLinkInfo = cbLinkFound?.(oldLinkInfo);
        if (!newLinkInfo?.slug && oldLinkInfo.oldDocName?.startsWith('SEE ALSO')) {
          // This is where the user has just messed up and added "SEE ALSO" to the document name.
          oldLinkInfo.oldDocName = oldLinkInfo.oldDocName.replace(/^SEE ALSO\s*/i, '').trim();
          newLinkInfo = cbLinkFound?.(oldLinkInfo);
        }
        if (newLinkInfo) {
          newLinkInfo.pageName ||= oldLinkInfo.pageName; // pageName shouldn't have changed
          // If we have no currentDocName, then use oldDocName
          newLinkInfo.currentDocName ||= newLinkInfo.oldDocName || oldLinkInfo.oldDocName;
          newLinkInfo.oldDocName ||= oldLinkInfo.oldDocName;
        } else {
          // Just keep the old info
          newLinkInfo = {
            oldDocName: oldLinkInfo.oldDocName,
            currentDocName: oldLinkInfo.oldDocName,
            slug: oldLinkInfo.slug,
            pageName: oldLinkInfo.pageName,
          };
        }
      } else if (oldLinkInfo) {
        // The can be a link like href="##Some Page" with no slug or oldDocName, meaning it is referring to the current document
        newLinkInfo = {
          pageName: oldLinkInfo.pageName,
        };
      }
      if (newLinkInfo?.slug || oldLinkInfo?.slug) {
        a.setAttribute('data-slug', newLinkInfo?.slug || oldLinkInfo?.slug!);
      }
      if (newLinkInfo || (oldLinkInfo && oldLinkInfo.slug)) {
        a.setAttribute('href', updateHrefWithLinkInfo(href, newLinkInfo || oldLinkInfo));
      }

      if (newLinkInfo) {
        updateLinkTextContent(a, newLinkInfo);

        if (newLinkInfo.currentDocName) {
          a.setAttribute('data-doc-name', newLinkInfo.currentDocName);
        } else {
          a.removeAttribute('data-doc-name');
        }
      }
      if (!newLinkInfo?.slug) {
        cbProblemFound?.(
          `Could not find slug: ${newLinkInfo?.currentDocName || newLinkInfo?.oldDocName || oldLinkInfo.oldDocName}`
        );
      }
    } else {
      a.setAttribute('target', '_blank');
      a.classList.add('external-link');
      a.classList.remove('internal-link');
    }
  });
}

/** Update the `data-slug` attribute  in <embeddocumentpage> to be the slug name of the document */
function updateEmbeddocumentpage(
  div: HTMLDivElement,
  cbLinkFound?: (slug: SlugInfoType) => SlugInfoType | undefined,
  cbProblemFound?: (problem: string) => void
) {
  div.querySelectorAll('embeddocumentpage').forEach((a) => {
    let slug: string | undefined = a.getAttribute('data-slug') || undefined;
    if (slug === 'undefined') {
      a.removeAttribute('data-slug'); // This was a bug in the past
      slug = undefined;
    }
    const doc = a.getAttribute('data-doc');
    if (!doc) {
      // Something very wrong - remove entirely
      div.outerHTML = '';
      return;
    }
    const pageName = a.getAttribute('data-page') || undefined;
    const docId = a.getAttribute('data-doc-id') || undefined;
    const linkInfo = cbLinkFound?.({ oldDocName: doc, docId, slug, pageName });
    if (linkInfo?.slug) {
      a.setAttribute('data-slug', linkInfo?.slug);
    }
    if (linkInfo?.currentDocName) {
      a.setAttribute('data-doc', linkInfo.currentDocName);
    }
    if (linkInfo?.docId && linkInfo.docId !== docId) {
      a.setAttribute('data-doc-id', linkInfo.docId);
    }
  });
}

/** Update the document name inside of an 'a' tag based on the new link info.
 *
 *  We want to allow custom text, so be careful about when we update.
 *  We only want to update if the text is one of:
 *   - document name
 *   - "SEE ALSO" + document name
 *   - document name + page name
 *   - "SEE ALSO" + document name + page name
 *
 * Otherwise, we leave the text alone.
 */
export function updateLinkTextContent(a: HTMLAnchorElement, newLinkInfo: SlugInfoType, tryToMovePunctuation = false) {
  let oldDocName = newLinkInfo?.oldDocName?.toLowerCase().replace(/&nbsp;/g, ' '); // Don't trim
  if (!oldDocName && !newLinkInfo?.pageName) {
    // Note oldDocName can be empty if it's a link to the current document with just a page name
    return;
  }
  let innerTextLower = a.innerText.trim().toLowerCase().replace(/\s/g, ' '); // Replace non-breaking spaces with spaces
  // Check for punctuation at the end of the innerText
  const endingPunctuationMatch = tryToMovePunctuation ? innerTextLower.match(/^(.*)([.,;:!?\- )]+)$/) : null;
  if (endingPunctuationMatch) {
    innerTextLower = endingPunctuationMatch[1]!;
    // We will move the punctuation outside the link after we know we are updating the link text
  }
  // Doesn't match our expected patterns, so leave alone
  //a.insertAdjacentText('afterend', text);
  const hasSeeAlso = innerTextLower.startsWith('see also ');
  if (hasSeeAlso) {
    innerTextLower = innerTextLower.substring(8).trim();
    // Sometimes we have two SEE ALSOs. Remove the second too
    if (innerTextLower.startsWith('see also ')) {
      innerTextLower = innerTextLower.substring(8).trim();
    }
  }

  const pageName = newLinkInfo?.pageName?.toLowerCase();
  let hasDocName = false;
  let hasPageName = false;

  if (pageName && innerTextLower === `${pageName}`) {
    hasPageName = true;
  } else if (
    pageName &&
    oldDocName &&
    // TODO replace with a regex to also check if we end with punctuation - in which case we should move the punctuation outside the link
    (innerTextLower === `${oldDocName}: ${pageName}` ||
      innerTextLower === `${oldDocName} - ${pageName}` ||
      innerTextLower === `${oldDocName} ${pageName}`)
  ) {
    hasDocName = true;
    hasPageName = true;
  } else if (innerTextLower === oldDocName) {
    hasDocName = true;
  } else {
    if (!tryToMovePunctuation) {
      // Call again with tryToMovePunctuation true
      updateLinkTextContent(a, newLinkInfo, true);
    }
    return;
  }

  let htmlContent = hasSeeAlso ? 'SEE ALSO ' : '';
  if (hasDocName) {
    htmlContent += simpleTextToHtml(newLinkInfo.currentDocName || newLinkInfo.oldDocName!);
  }
  if (hasPageName) {
    if (hasDocName) htmlContent += ': ';
    htmlContent += `<i>${simpleTextToHtml(newLinkInfo.pageName!)}</i>`;
  }
  a.innerHTML = htmlContent;

  // If we had ending punctuation, move it outside the link
  if (endingPunctuationMatch && endingPunctuationMatch[2]) {
    a.insertAdjacentText('afterend', endingPunctuationMatch[2]);
  }

  const updateText = determineLinkUpdateText(hasSeeAlso, hasDocName, hasPageName);
  if (updateText) {
    a.setAttribute('data-update-text', updateText);
  } else {
    a.removeAttribute('data-update-text');
  }
}

/** Returns one of:
 * - see-also-with-page
 * - see-also
 * - see-also-with-page-without-doc
 * - without-see-also-with-page
 * - without-see-also-or-doc-with-page
 * - without-see-also
 * or null if it can't be determined
 */
function determineLinkUpdateText(hasSeeAlso: boolean, hasDocName: boolean, hasPageName: boolean): string | null {
  if (hasSeeAlso) {
    if (hasPageName) {
      if (hasDocName) {
        return 'see-also-with-page';
      } else {
        return 'see-also-with-page-without-doc';
      }
    } else {
      return 'see-also';
    }
  } else {
    if (hasPageName) {
      if (hasDocName) {
        return 'without-see-also-with-page';
      } else {
        return 'without-see-also-or-doc-with-page';
      }
    } else if (hasDocName) {
      return 'without-see-also';
    }
  }
  return null;
}

/** In unit tests, DISABLE_ACTION_ID_GENERATION can be set to true to prevent action ID generation
 *  for easier snapshot testing
 */
var DISABLE_ACTION_ID_GENERATION = false;
export function setDisableActionIdGenerationForTests(disable: boolean) {
  DISABLE_ACTION_ID_GENERATION = disable;
}

/* This runs on load, and also after drag and drop */
export function cleanupDom(node: HTMLElement): { duplicatedFhirIds: { oldId: string; newId: string }[] } {
  node.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((element) => {
    const normalizedText = element.textContent?.replace(/\u00a0/g, ' ').trim();
    const hasNonTextContent = !!element.querySelector('img, action, fhir, oembed, iframe, video, audio, table, ul, ol');
    if (!normalizedText && !hasNonTextContent) {
      element.replaceWith(document.createElement('div'));
    }
  });

  node.querySelectorAll('fhir').forEach((element) => {
    const nextSibling = element.nextSibling;
    const suffix =
      nextSibling?.nodeType === Node.TEXT_NODE ? nextSibling.textContent?.match(/^[A-Za-z0-9]+/)?.[0] : undefined;
    if (!suffix || !/[A-Za-z0-9]$/.test(element.textContent || '')) return;
    element.append(suffix);
    nextSibling!.textContent = nextSibling!.textContent!.substring(suffix.length);
    if (!nextSibling!.textContent) nextSibling!.remove();
  });

  // When dragging actions, you can drag an action inside of <action> (rather than actioncontent).
  // Clean up by moving it outside
  node.querySelectorAll('action > :not(actioncontent)').forEach((element: Element) => {
    element.parentElement!.after(element);
  });
  // when we have an action as the only child of a div, we can't drag actions next to each other
  node.querySelectorAll('div > action:only-child').forEach((element: Element) => {
    if (!(element.parentElement! as HTMLDivElement).textContent?.length) {
      element.parentElement!.after(element);
    }
  });
  // We collapse a span with only spans inside. E.g. <span><span>text</span></span> -> <span>text</span>
  node.querySelectorAll('span:only-child').forEach((element: Element) => {
    const parent = element.parentElement!;
    if (parent.tagName === 'SPAN') {
      element.outerHTML = element.innerHTML;
    }
  });
  // Remove any empty <div class="llm-visible-comment"></div> These can be left behind when deleting comments
  node.querySelectorAll('div.llm-visible-comment:empty').forEach((element: Element) => {
    element.remove();
  });
  // Unwrap all spans with no attributes
  // Commneted out because this is going to affect a lot of documents
  // node.querySelectorAll('span').forEach((element: Element) => {
  //   if (element.attributes.length === 0) {
  //     element.outerHTML = element.innerHTML;
  //   }
  // });

  // We can get empty a links when selecting an a-link and creating an action
  node.querySelectorAll('a:empty').forEach((element: Element) => {
    element.remove();
  });
  // Unwrap a links with no href
  node.querySelectorAll('a:not([href])').forEach((element: Element) => {
    element.outerHTML = element.innerHTML;
  });
  // I don't know how we get these. <action> should always have a child <actioncontent>
  node.querySelectorAll('action:empty').forEach((element: Element) => {
    element.remove();
  });
  // We can get empty fhir links when deleting all the text inside
  node.querySelectorAll('fhir:empty').forEach((element: Element) => {
    element.remove();
  });
  // remove blank lines at start and end of pearltag.  E.g.:
  // <pearltag data-tag="pregnancy" data-value="" id="1145205078755368"><div>&nbsp;</div><span>Hydrocortisone administration is mostly inactivated in the placenta and has no adverse effects on the fetus.</span>
  node.querySelectorAll('pearltag').forEach((element: Element) => {
    function removeIfBlank(el: Element) {
      if (
        (el?.tagName === 'DIV' || el?.tagName === 'SPAN') &&
        /* has no children and has no text */
        !el.textContent?.trim() &&
        !el.firstElementChild
      ) {
        el.remove();
        return true;
      }
      return false;
    }
    let child = element.firstElementChild;
    while (removeIfBlank(child!)) {
      child = element.firstElementChild;
    }
    child = element.lastElementChild;
    while (removeIfBlank(child!)) {
      child = element.lastElementChild;
    }
  });

  // Delete the ondblclick attribute from all elements.  DOMPurify removes it but still better to remove
  node.querySelectorAll('[ondblclick]').forEach((element: Element) => {
    element.removeAttribute('ondblclick');
  });

  // Remove id from divs.  At one point we lots of <div id="html-rendered"> that came from people copying and pasting from rendered tinymce into the editor
  // Delete all attributes on div elements.  We don't use them and we have a lot of aria and data attributes that
  // come from copy-pasting from other sources
  node.querySelectorAll('div').forEach((div) => {
    // Remove all attributes
    Array.from(div.attributes).forEach((attr) => {
      // Except for class="llm-visible-comment"
      if (
        attr.name === 'class' &&
        (attr.value === 'llm-visible-comment' ||
          attr.value === 'qr-code-container' ||
          attr.value === 'pearltag-disclaimer')
      ) {
        return;
      }
      div.removeAttribute(attr.name);
    });
  });

  // Ensure ids are unique across the whole document. Duplicate ids break DOM lookup,
  // including FHIR hover/callout behavior.
  const seenIds = new Set<string>();
  const duplicateIdCounters: Record<string, number> = {};
  const duplicatedFhirIds: { oldId: string; newId: string }[] = [];
  node.querySelectorAll('[id]').forEach((element) => {
    let id = element.getAttribute('id');
    if (!id) {
      return;
    }
    const originalId = id;
    if (seenIds.has(id)) {
      if (DISABLE_ACTION_ID_GENERATION) {
        // Give duplicate ids test-stable replacements without consuming mocked uuid values,
        // so unrelated snapshot ids keep their existing numbering.
        const prefix =
          element.tagName === 'ACTION' ? 'a' : element.tagName === 'FHIR' ? 'f' : `${element.tagName.toLowerCase()}-`;
        const n = duplicateIdCounters[prefix] || 0;
        duplicateIdCounters[prefix] = n + 1;
        id = `${prefix}123456789-${n}`;
      } else {
        const prefix =
          element.tagName === 'ACTION' ? 'a' : element.tagName === 'FHIR' ? 'f' : `${element.tagName.toLowerCase()}-`;
        id = `${prefix}${uuidV4()}`;
      }
      element.setAttribute('id', id);
      if (element.tagName === 'FHIR') {
        duplicatedFhirIds.push({ oldId: originalId, newId: id });
      }
    }
    seenIds.add(id);
  });
  return { duplicatedFhirIds };
}

function duplicateRemappedFhirConfig(fhir: PageFhirType, duplicatedFhirIds: { oldId: string; newId: string }[]) {
  duplicatedFhirIds.forEach(({ oldId, newId }) => {
    const existing = fhir.idsToFhir[oldId];
    if (!existing || fhir.idsToFhir[newId]) {
      return;
    }
    fhir.idsToFhir[newId] = {
      ...JSON.parse(JSON.stringify(existing)),
      id: newId,
    };
  });
}

/** Update the following attribute on a <fhir> tag:
 *
 *  See CKFhirPlugin.tsx for the view function that creates the fhir tag:
 *
 * 'data-fhir-warning': predefinedWidgetId ? '' : '(Not configured)',
 * 'data-fhir-widget-id': predefinedWidgetId || '',
 * 'data-fhir-widget-name': predefinedWidgetName || '',
 * title: predefinedWidgetName || '(Not configured)',
 *
 */
function updateFhirAttributes(
  div: HTMLDivElement,
  fhir: PageFhirType,
  predefinedWidgets:
    | {
        [id: string]: FhirPredefinedWidget;
      }
    | undefined = undefined // TODO - implement this
) {
  if (Object.keys(fhir.idsToFhir).length === 0) {
    // No fhir info - either not loaded for some reason, or just testing
    // Don't change fhir tags
    return;
  }
  // Find every <fhir> tag, and update the attributes
  div.querySelectorAll('fhir').forEach((node) => {
    const fhirInfo = fhir.idsToFhir[node.getAttribute('id') || ''];
    const predefinedWidgets_ = fhirInfo?.predefinedWidgets;

    if (!predefinedWidgets_ || predefinedWidgets_.length === 0) {
      node.setAttribute('data-fhir-warning', '(Not configured)');
      node.removeAttribute('data-fhir-widget-id');
      node.removeAttribute('title');
      node.removeAttribute('data-fhir-widget-name');
    } else {
      const predefinedWidgetName = predefinedWidgets_
        .map((pw) => predefinedWidgets?.[pw.id]?.name)
        .filter(Boolean)
        .join(',');
      const predefinedWidgetIds = predefinedWidgets_.map((pw) => pw.id).join(';');
      node.removeAttribute('data-fhir-warning');
      if (node.getAttribute('data-fhir-widget-id') !== predefinedWidgetIds) {
        node.setAttribute('data-fhir-widget-id', predefinedWidgetIds);
        if (predefinedWidgetName) {
          // currently not implemented, so not set
          node.setAttribute('data-fhir-widget-name', predefinedWidgetName);
          node.setAttribute('title', predefinedWidgetName);
        } else {
          node.removeAttribute('data-fhir-widget-name');
          node.removeAttribute('title');
        }
      }
    }
  });
}

function combineBulletPoints(content: string): string {
  // Keep doing this until the content doesn't change.
  let lastContent = content;
  while (true) {
    // When we have <ul><li>...</li></ul><ul><li>...</li></ul> we can combine them into a single <ul> with multiple <li>
    // First, remove any empty <ul> tags
    content = content.replace(/<ul>(?:\n|\s)*<\/ul>/g, '');
    // Now, combine any adjacent <ul> tags, as long as there isn't a <div> between them
    content = content.replace(/\n*<\/ul>(\s|\n|<\/div>(&nbsp;|\s|\n)*<div>)*<ul>/g, '');

    // Combine any adjacent <ul> tags with a <div> between them if the first <li> is list-style-type: none
    content = content.replace(
      /\n*<\/ul>(\s|\n|<div>(&nbsp;|\s|\n)*<\/div>)*<ul>(\s|\n)*<li style="list-style-type: none;">/g,
      '<li style="list-style-type: none;">'
    );
    // Todo, combine when we have: <li>Foo</li><li style="list-style-type: none;"><ul>...</ul></li>
    // It should become: <li>Foo<ul>...</ul></li>
    content = content.replace(/<\/ul>(\s|\n)*<\/li>(\s|\n)*<li style="list-style-type: none;">(\s|\n)*<ul>/g, '');
    content = content.replace(/<\/li>(\s|\n)*<li style="list-style-type: none;">(\s|\n)*<ul>/g, '\n<ul>');
    content = content.replace(/<\/li>\n\n+<li>/g, '</li>\n<li>');
    // </li>\n<li>&nbsp;\n<ul>\n<li> -> <ul>\n<li>
    content = content.replace(
      /<\/li>(\s|\n)*<li( style="list-style-type: none;")?>(&nbsp;|\s|\n)*<ul>(\s|\n)*<li>/g,
      '<ul>\n<li>'
    );
    // Remove any <li></li> with only whitespace or &nbsp; in it
    content = content.replace(/<li>(?:\s|&nbsp;)*<\/li>/g, '');
    // Remove any <ul></ul> with only whitespace or &nbsp; in it
    content = content.replace(/<ul>(?:\s|&nbsp;)*<\/ul>/g, '');
    // ditto for <ol>
    content = content.replace(/<ol>(?:\s|&nbsp;)*<\/ol>/g, '');
    if (content === lastContent) {
      break;
    }
    lastContent = content;
  }
  return content;
}

/* Hmm we already have this exact function escapeHtml in utils.ts */
function simpleTextToHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;') // Escape ampersands
    .replace(/</g, '&lt;') // Escape less-than signs
    .replace(/>/g, '&gt;'); // Escape greater-than signs
  // lodash escape escapes too much (e.g. quotes) so we don't use it, just to keep unit tests stable
}

function fixupPageNextLinks<T extends PageType>(
  page: T,
  cbLinkFound?: (slug: SlugInfoType) => SlugInfoType | undefined
): T {
  if (pageCanHaveNextLink(page)) {
    updateLinkAndSlugInObject(page, 'next_link', 'next_link_slug', cbLinkFound);
  }
  if (page.pageType === PageTypeEnum.SWIMLANE) {
    updateLinkAndSlugInObject(page as PageSwimLaneType, 'fallback_link', 'fallback_link_slug', cbLinkFound);
    // Update the links in the lanes
    page.lanes?.forEach((lane) => {
      updateLinkAndSlugInObject(lane, 'link', 'link_slug', cbLinkFound);
    });
  } else if (page.pageType === PageTypeEnum.IF) {
    // This has a true_link and false_link that need to be updated to have the slug
    updateLinkAndSlugInObject(page as PageIfType, 'true_link', 'true_link_slug', cbLinkFound);
    updateLinkAndSlugInObject(page as PageIfType, 'false_link', 'false_link_slug', cbLinkFound);
  } else if (page.pageType === PageTypeEnum.CALCULATOR) {
    page.formulaBins?.forEach((bin) => {
      updateLinkAndSlugInObject(bin, 'link', 'link_slug', cbLinkFound);
    });
  }
  // Note updateButtonSlug also updates the link and linkSlug for buttons in calculator and multiple questions
  return page;
}

/**
 *  We renamed the key and value variables in a function to fix bad naming and prevent mistakes
 *  when converting between single-select and multi-select questions
 */
function fixupQuestions<T extends PageType>(
  page: T,
  saving: boolean,
  cbLinkFound?: (slug: SlugInfoType) => SlugInfoType | undefined
): T {
  if (!(page.pageType === PageTypeEnum.MULTIPLE_QUESTIONS || page.pageType === PageTypeEnum.CALCULATOR)) return page;
  for (const question of (page as PageMultipleQuestionsType | PageCalculatorType).questions || []) {
    if ((question as any).question && !question.questionHtml) {
      // Legacy - question used to be only plain text, now it's html
      // So convert the text to html - i.e. escape < and > etc
      (question as any).question = (question as any).question.trim();
      question.questionHtml = simpleTextToHtml((question as any).question);
    } else {
      // Convert the question to plain text, for backwards compatibility.
      // The mobile app needs to be updated to use questionHtml
      (question as any).question = question.questionHtml ? compiledHtmlConvertPlain(question.questionHtml)?.trim() : '';
    }
    if (questionCanHaveOptions(question)) {
      if (question.type === 'exclusive') {
        question.exclusive_key ||= getStableExclusiveQuestionIdFromQuestionId(question);
        for (const option of question.options || []) {
          updateButtonSlug(option, cbLinkFound);
          // Make sure it has an id
          option.id ||= createRandomAlphaNumericId();
          option.fhirWidgetId ||= `b${option.id}`; // Make sure it starts with a letter
          option.exclusive_value ||= (option as any).value || option.text.toUpperCase();
          if (option.exclusive_value !== option.text.toUpperCase()) {
            // Save to the value field when saving just for backwards compatibility - the mobile app needs to be updated
            // When everything is updated, we could delete this line.
            // Note: We now save the field even when not saving, so that we don't accidentally change the order of the
            // object when loading and then saving.
            // FIXME: Remove when mobile is updated
            if (saving || (option as any).value) (option as any).value = option.exclusive_value;
          } else {
            delete (option as any).value;
          }
        }
      } else if (question.type === 'multiple_choice') {
        for (const option of question.options || []) {
          updateButtonSlug(option, cbLinkFound);
          // Make sure it has an id
          option.id ||= createRandomAlphaNumericId();
          option.fhirWidgetId ||= `b${option.id}`; // Make sure it starts with a letter
          // multiple_choice_key used to be incorrectly called exclusive_key, making it very confusing!!
          option.multiple_choice_key ||= (option as any).exclusive_key || option.text.toUpperCase();
          // We used to have a multiple_choice_value, but just delete because it caused bugs
          delete (option as any).multiple_choice_value;
          delete (option as any).value;
          // Save to the exclusive_key and value field just for backwards compatibility - the mobile app needs to be updated
          // When everything is updated, we could delete the following lines.
          if (option.multiple_choice_key !== option.text.toUpperCase()) {
            (option as any).exclusive_key = option.multiple_choice_key;
          } else {
            delete (option as any).exclusive_key;
          }
        }
      }
    }
  }
  return page;
}

function getStableExclusiveQuestionIdFromQuestionId(question: PageQuestionExclusiveType): string | undefined {
  const questionId = question.id?.trim();
  if (!questionId) return undefined;
  // Most real documents already have a stable random-ish question.id. Very short numeric ids are the
  // older index-based fallback, so only promote ids that are already plausibly stable.
  if (!questionId.match(/^\d+$/) || questionId.length > 3) {
    return questionId;
  }
  return undefined;
}

function migrateLegacyQuestionKeyReferences<T extends DocumentVersionWithoutGeneratedFieldsType>(
  docVersion: T,
  dirty: string[]
): string[] {
  const legacyToStable = new Map<string, string>();
  const ambiguousLegacyKeys = new Set<string>();
  const modifiedPageIds = new Set<string>();

  Object.values(docVersion.pages).forEach((page) => {
    if (page.pageType !== PageTypeEnum.MULTIPLE_QUESTIONS && page.pageType !== PageTypeEnum.CALCULATOR) return;
    page.questions?.forEach((question) => {
      if (question.type !== 'exclusive') return;
      const legacyKey = getLegacyKeyForExclusiveQuestion(question, page).toUpperCase();
      const stableKey = getKeyForExclusiveQuestion(question, page);
      const stableQuestionId = getStableExclusiveQuestionIdFromQuestionId(question)?.toUpperCase();
      if (!stableQuestionId || stableKey !== stableQuestionId) return;
      if (!legacyKey || legacyKey === stableKey) return;
      const existing = legacyToStable.get(legacyKey);
      if (existing && existing !== stableKey) {
        ambiguousLegacyKeys.add(legacyKey);
        legacyToStable.delete(legacyKey);
        return;
      }
      if (!ambiguousLegacyKeys.has(legacyKey)) {
        legacyToStable.set(legacyKey, stableKey);
      }
    });
  });

  if (legacyToStable.size === 0) return [];

  let migratedSwimlaneChoices = 0;
  let migratedIfVariables = 0;

  Object.values(docVersion.pages).forEach((page) => {
    if (page.pageType === PageTypeEnum.SWIMLANE) {
      page.lanes?.forEach((lane) => {
        Object.entries(lane.choices).forEach(([legacyKey, value]) => {
          const stableKey = legacyToStable.get(legacyKey.toUpperCase());
          if (!stableKey || stableKey === legacyKey) return;
          if (lane.choices[stableKey] == null) {
            lane.choices[stableKey] = value;
          }
          delete lane.choices[legacyKey];
          migratedSwimlaneChoices += 1;
          modifiedPageIds.add(page.id);
        });
      });
    } else if (page.pageType === PageTypeEnum.IF && page.variable) {
      const stableKey = legacyToStable.get(page.variable.toUpperCase());
      if (stableKey && stableKey !== page.variable) {
        page.variable = stableKey;
        migratedIfVariables += 1;
        modifiedPageIds.add(page.id);
      }
    }
  });

  if (migratedSwimlaneChoices > 0 || migratedIfVariables > 0) {
    const changes = [];
    if (migratedSwimlaneChoices > 0) changes.push(`${migratedSwimlaneChoices} swimlane choice key(s)`);
    if (migratedIfVariables > 0) changes.push(`${migratedIfVariables} if-variable reference(s)`);
    dirty.push(`Migrated legacy text-based question keys to stable ids for ${changes.join(' and ')}`);
  }

  return [...modifiedPageIds];
}

/**
 *  Keep option.link and option.linkSlug in sync.
 *
 *  - If option.linkSlug is set, then option.link should start with # and be updated to match the name of that document
 *  - If option.linkSlug is not set, but option.link starts with #, then find the slug for the document and set option.linkSlug to the name of the document
 *
 * If the creator wants to update option.link (e.g. in OptionsEditor) then it's their responsibility to update option.linkSlug or set it to undefined
 * and let it be set on save.
 */
function updateButtonSlug(
  option: PageOptionExclusiveType & PageOptionMultipleChoiceType,
  cbLinkFound?: (slug: SlugInfoType) => SlugInfoType | undefined
) {
  updateLinkAndSlugInObject(option, 'link', 'linkSlug', cbLinkFound);
}

/** Helper that will set the link and slug in the given object */
function updateLinkAndSlugInObject<T extends Record<string, any>, K extends keyof T, S extends keyof T>(
  obj: T & Partial<Record<K | S, string | undefined>>, // Enforce values are strings
  link_key: K & string,
  link_slug_key: S & string,
  cbLinkFound?: (slug: SlugInfoType) => SlugInfoType | undefined
) {
  const res = updateLink(obj[link_key], obj[link_slug_key], cbLinkFound);
  // if statement is just to not needlessly change the object from having no key to undefined
  if (res?.link || obj[link_key]) {
    obj[link_key] = res?.link as T[K & string];
  }
  if (res?.link_slug) {
    obj[link_slug_key] = res.link_slug as T[S & string];
  } else {
    delete obj[link_slug_key];
  }
}

/** Update a link and link_slug pair
 *
 *  - If link_slug is undefined and link is not an internal link, then return undefined
 *  - If link_slug is undefined and link is an internal link, then call cbLinkFound with the oldDocName to get the slug
 *  - If link_slug is defined, then call cbLinkFound with the slug to get the currentDocName
 *
 *  Returns updated { link, link_slug } or undefined if no link
 */
function updateLink(
  link: string | undefined,
  link_slug: string | undefined,
  cbLinkFound?: (slug: SlugInfoType) => SlugInfoType | undefined
): LinkSlugType | undefined {
  if (!link_slug && !link?.startsWith('#')) {
    link_slug = undefined;
    if (!link) return undefined;
    return { link, link_slug };
  }
  // link is like #foo or #foo#bar  where foo is the doc name and bar is the page name. Split into two parts
  const [oldDocName, pageName] = link?.substring(1)?.split('#', 2) || [];
  if (!link_slug) {
    // Update link_slug using the name of the document
    const newLinkInfo = cbLinkFound?.({ oldDocName: oldDocName?.trim() });
    if (newLinkInfo?.slug) {
      link_slug = newLinkInfo.slug;
      link = newLinkInfo?.currentDocName
        ? '#' + newLinkInfo?.currentDocName + (pageName ? '#' + pageName.trim() : '')
        : link?.replace(/ #/g, '#');
    }
  } else {
    const newLinkInfo = cbLinkFound?.({ slug: link_slug, oldDocName: oldDocName?.trim() });
    if (newLinkInfo?.currentDocName) {
      link = '#' + newLinkInfo.currentDocName + (pageName ? '#' + pageName.trim() : '');
    }
  }
  return link ? { link, link_slug } : undefined;
}

/** content is html.  When we have a tag like:  <action .. title="Foo" ..>  remove the title */
function removeTooltipFromActionIcon(content: string): string {
  const removeTooltipFromActionIconRegexp = /<action (?:([^>]*) |)title="([^"]*)"([^>]*)>/g;
  return content.replace(removeTooltipFromActionIconRegexp, '<action $1$3>');
}

function removeBadHtml(content: string): string {
  // If there is a <a> link with only white space (including &nbsp;, <br/> etc), then remove it.
  content = content.replace(/<a [^>]*>(&nbsp;|\s)*<\/a>/g, '$1');
  // If there is a <a> link with just a <br/>, remove the <a> link and keep the <br/>
  content = content.replace(/<a [^>]*>(&nbsp;|\s)*<br ?\/?>(&nbsp;|\s)*<\/a>/g, '<br/>');
  // If we have <a>foo </a>  then move the space or punctuation outside the <a> tag
  content = content.replace(/<a ([^>]*)>([^<]*)((?:&nbsp;|\s|[.,:]|<br ?\/?>)+)<\/a>/g, '<a $1>$2</a>$3');
  // If we have <a> foo</a> then move the space outside the <a> tag
  content = content.replace(/<a ([^>]*)>(?:&nbsp;|\s)+([^<]*)<\/a>/g, ' <a $1>$2</a>');
  // If we have "SEE ALSO <a class="internal-link">foo</a>" then move the "SEE ALSO" inside the <a> tag"
  content = content.replace(/SEE ALSO(?:&nbsp;|\s|:)*(<a [^>]*>)(?:&nbsp;|\s|:)*/gi, '$1 SEE ALSO ');

  // Combine </em> <em> to just a space
  content = content.replace(/<\/em>((?:&nbsp;|\s)*)<em>/g, '$1');

  // Same as all the above combined, just more efficient, using backreference
  // and keeping the whitespace if there is any
  content = content.replace(/<(strong|em|span)>(&nbsp;|\s)*<\/\1>/g, '$2');
  // Remove comments <!-- foo -->
  content = content.replace(/<!--.*?-->/g, '');
  // Simplify <span>foo</span><span>  to <span>foo
  content = content.replace(/<span>([^<]*)<\/span><span>/g, '<span>$1');
  // Change kg/m2 to kg/m²
  content = content.replace(/kg\/m2/g, 'kg/m²');
  return content;
}

// Look through the content, and convert text like:
//  \\u003c to <
//  \\u003e to >
//  \\u0026 to &
//  \\u0027 to '
//  \\u0022 to "
//  \\u00a0 to non-breaking space
// Where \\  means a single literal \
// I don't know why it can end up like this
function unicodeToChar(text: string) {
  return text.replace(/\\u[\dA-F]{4}/gi, function (match) {
    return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
  });
}

function fixChoosingWiselyLinks(content: string): string {
  // Look through the content, and convert links like:
  //  <a href="https://www.choosingwisely.org/clinician-lists/society-nuclear-medicine-molecular-imaging-nuclear-medicine-thyroid-scans-to-evaluate-thyroid-nodules/?highlight=nuclear%20medicine%20thyroid">
  // to the web archive version, and removing any query  - e.g. ?highlight=nuclear%20medicine%20thyroid
  //  <a href="https://web.archive.org/web/https://www.choosingwisely.org/clinician-lists/society-nuclear-medicine-molecular-imaging-nuclear-medicine-thyroid-scans-to-evaluate-thyroid-nodules/">
  // This is because choosing wisely has shut down their website, and we need to use the web archive version.
  return content?.replace(
    / href="(https?:\/\/www[.]choosingwisely[.]org\/[^"?]*)(?:[?][^"]*)?"/g,
    ' href="https://web.archive.org/web/$1"'
  );
}

function decodeMaybePrefixedBase64Url(s: string): string | undefined {
  const tryDecodeURI = (v: string) => {
    try {
      return decodeURIComponent(v);
    } catch {
      return v;
    }
  };

  let v = tryDecodeURI(s);

  // If it's already an http URL, just return it
  if (/^https?:\/\//i.test(v)) return v;

  // Try progressively stripping leading chars until base64 decodes
  for (let i = 0; i < Math.min(5, v.length); i++) {
    const candidate = v.slice(i).replace(/-/g, '+').replace(/_/g, '/');
    try {
      let decoded: string;
      if (typeof atob === 'function') {
        decoded = atob(candidate);
      } else {
        decoded = Buffer.from(candidate, 'base64').toString('utf8');
      }
      if (/^https?:\/\//i.test(decoded)) return decoded;
    } catch {
      /* ignore */
    }
  }

  return undefined; // If we can't decode it, return undefined
}

function bingImageToDirectLink(href: string): string | null {
  try {
    const url = new URL(href);
    if (!/(\.|^)bing\.com$/i.test(url.hostname)) return href;

    const p = url.searchParams;
    const path = url.pathname;

    // Image detail pages
    if (path.startsWith('/images/search')) {
      const mediaUrl = p.get('mediaurl') || p.get('imgurl');
      if (mediaUrl) return decodeURIComponent(mediaUrl);
    }

    // Click tracker
    if (path.startsWith('/ck/a')) {
      const u = p.get('u') || p.get('r') || p.get('url');
      if (u) {
        const ret = decodeMaybePrefixedBase64Url(u);
        console.log('bing image ck link found.  Decoding from:', u, 'to:', ret);
        if (ret) return ret;
      }
    }

    return href;
  } catch {
    return href;
  }
}

/** Fix if a page has the name of a standard guide, but wrong pageType */
function fixupPageGuides<T extends PageType>(page: T): T {
  if (page.pageType === PageTypeEnum.GUIDE) {
    return page;
  }
  // Page is not guide..
  const shouldBeGuide = standardGuides.indexOf(page.name) !== -1;
  if (!shouldBeGuide) {
    return page;
  }

  // But it should be. What to do?  Lets.. um..  make it a guide.
  // We prevent new guides from being created with the same name as a standard guide,
  // but there can be legacy documents with this problem.
  (page as any as PageGuideType).pageType = PageTypeEnum.GUIDE;
  page.isRoot = true;
  return page;
}

/** Add an order number to guides, for the order we show them in tabs. */
function fixupPageIds<T extends PageType>(page: T): T {
  if (page.pageType === PageTypeEnum.GUIDE) {
    page.order ||= standardGuides.indexOf(page.name);
  } else if (page.pageType === PageTypeEnum.SWIMLANE) {
    page.lanes?.forEach((lane) => {
      if (lane.id == null) lane.id = uuidV4();
    });
  } else if (page.pageType === PageTypeEnum.MULTIPLE_QUESTIONS || page.pageType === PageTypeEnum.CALCULATOR) {
    page.questions?.forEach((question, index) => {
      question.id ??= index.toString();
      if (questionCanHaveOptions(question)) {
        question.options.forEach((option, optionIndex) => (option.id ??= optionIndex.toString()));
      }
    });
  }
  return page;
}

/** Before 2023/03/14 there was no scope and matching on fhir rule, and instead it was just part of the .name string.
 *  This function fixes that, parsing a name like 'o:.*Hemoglobin.*' into a name of 'Hemoglobin'
 *  and a scope of 'Observation' and a matching of 'Partial'.
 *
 *  On 2025/09/3 we removed isPredefinedWidget and move predefinedWidget and observationGroup into
 *  predefinedWidgets array to allow multiple predefined widgets per fhir entry.
 *
 * @see FhirPageWidgetType Which also documents the changes to itself
 */
export function fixupPageFhir<T extends PageFhirType | undefined>(fhir: T): T {
  if (fhir?.idsToFhir) {
    Object.values(fhir?.idsToFhir).forEach((fhir) => {
      delete (fhir as any).ourObservations; // Remove the old style ourObservations
      delete (fhir as any).rules; // Remove the old style rules - this should only be in the predefined widgets
      delete (fhir as any).whitelist; // Remove the old style whitelist - this should only be in the predefined widgets
      delete (fhir as any).blacklist; // Remove the old style blacklist - this should only be in the predefined widgets
      if ((fhir as any).isPredefinedWidget === false) {
        // If it exists and is false, delete predefinedWidget
        delete (fhir as any).predefinedWidget;
      } else if ((fhir as any).predefinedWidget && !fhir.predefinedWidgets) {
        // We originally had just a single predefinedWidget for a fhir entry, but now we can have multiple.
        // So convert predefinedWidget to predefinedWidgets, as an array
        fhir.predefinedWidgets = [
          {
            /** fix up the id in predefinedWidgets. @see fixupWidgetId */
            id: fixupWidgetId((fhir as any).predefinedWidget),
          },
        ];
        if ((fhir as any).observationGroup) {
          fhir.predefinedWidgets[0]!.observationGroup = (fhir as any).observationGroup;
        }
      } else if (fhir.predefinedWidgets) {
        /** Just fix up the ids in predefinedWidgets. @see fixupWidgetId */
        fhir.predefinedWidgets = fhir.predefinedWidgets.map((pw) => ({
          ...pw,
          id: fixupWidgetId(pw.id),
        }));
      }
      if (!fhir.predefinedWidgets) fhir.predefinedWidgets = [];
      fhir.predefinedWidgets = simplifyFHIRPredefinedWidgets(fhir.predefinedWidgets);

      const LEGACY_MODE = true; // Set to true to keep backwards compatibility
      if (LEGACY_MODE && fhir.predefinedWidgets.length > 0) {
        // make predefinedWiget and observationGroup a copy of the first
        // item in predefinedWidgets, for backwards compatibility with older versions
        // of the app.
        // Remove 3 months after time of writing (now is 2025/09/04)
        (fhir as any).predefinedWidget = fhir.predefinedWidgets[0]!.id;
        if (fhir.predefinedWidgets[0]!.observationGroup) {
          (fhir as any).observationGroup = fhir.predefinedWidgets[0]!.observationGroup;
        } else {
          delete (fhir as any).observationGroup;
        }
      } else {
        delete (fhir as any).predefinedWidget;
        delete (fhir as any).observationGroup;
      }
      delete (fhir as any).isPredefinedWidget; // Removed - is always true now
    });
  }

  return fhir;
}

export function migrateDeprecatedFhirWidgets(
  fhir: PageFhirType | undefined,
  predefinedWidgetsMap: Record<string, FhirPredefinedWidget> | undefined
): boolean {
  if (!fhir?.idsToFhir || !predefinedWidgetsMap) return false;

  let changed = false;
  Object.values(fhir.idsToFhir).forEach((pageWidget) => {
    pageWidget.predefinedWidgets = pageWidget.predefinedWidgets.map((configuredWidget) => {
      const replacementId = resolveFhirPredefinedWidgetId(configuredWidget.id, predefinedWidgetsMap);
      if (!replacementId || replacementId === configuredWidget.id) return configuredWidget;
      changed = true;
      return { ...configuredWidget, id: replacementId };
    });
  });

  if (changed) fixupPageFhir(fhir);
  return changed;
}

// Remove duplicates from the predefinedWidgets array
export function simplifyFHIRPredefinedWidgets(
  widgets: FhirPageWidgetType['predefinedWidgets']
): FhirPageWidgetType['predefinedWidgets'] {
  const seen = new Set<string>();
  return widgets
    .filter((w) => {
      if (!w.id) return false; // Can happen when we've added a new empty widget but then not filled it in
      const key = JSON.stringify(w);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)); // Sort by id
}
